import { Link } from 'react-router-dom'
import { useLocale } from '../locale/LocaleContext.js'
import { legalDocuments } from './legalContent.js'

/// 公开法律文档页（/privacy、/terms）。正文为简体中文（面向中国区），
/// 与 iOS 应用内「服务条款 & 隐私政策」同源（见 legalContent.ts）。
/// 页面 chrome（分段/返回）随 locale 双语，正文不翻译。
export function LegalPage({ doc }: { doc: 'privacy' | 'terms' }) {
  const { locale, t } = useLocale()
  const zh = locale === 'zh'
  const document = legalDocuments[doc]

  const tab = (to: string, key: 'terms' | 'privacy', label: string) => (
    <Link
      to={to}
      className={
        doc === key
          ? 'font-semibold text-[var(--xr-text-primary)] underline underline-offset-4'
          : 'text-[var(--xr-text-muted)] transition hover:text-[var(--xr-text-primary)]'
      }
      aria-current={doc === key ? 'page' : undefined}
    >
      {label}
    </Link>
  )

  return (
    <div className="h-full overflow-y-auto bg-[var(--xr-bg-canvas)]">
      <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:py-12">
        <nav className="mb-6 flex flex-wrap items-center gap-3 text-[13px]">
          {tab('/terms', 'terms', zh ? '服务条款' : 'Terms of Service')}
          <span aria-hidden className="text-[var(--xr-text-muted)]">
            ·
          </span>
          {tab('/privacy', 'privacy', zh ? '隐私政策' : 'Privacy Policy')}
          <span aria-hidden className="text-[var(--xr-text-muted)]">
            ·
          </span>
          <Link
            to="/support"
            className="text-[var(--xr-text-muted)] transition hover:text-[var(--xr-text-primary)]"
          >
            {t('support.title')}
          </Link>
        </nav>

        <article>
          <h1 className="text-[22px] font-semibold tracking-tight text-[var(--xr-text-primary)]">
            {document.title}
          </h1>

          {document.intro.length > 0 && (
            <div className="mt-4 space-y-2">
              {document.intro.map((line, i) => (
                <Paragraph key={i} line={line} muted />
              ))}
            </div>
          )}

          <div className="mt-6 space-y-6">
            {document.sections.map((section) => (
              <section key={section.heading}>
                <h2 className="text-[15px] font-semibold text-[var(--xr-text-primary)]">
                  {section.heading}
                </h2>
                <div className="mt-2 space-y-2">
                  {section.body.map((line, i) => (
                    <Paragraph key={i} line={line} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </article>

        <div className="mt-10 text-[12px] text-[var(--xr-text-muted)]">
          <Link to="/discover" className="transition hover:text-[var(--xr-text-primary)]">
            {zh ? '← 返回寻径' : '← Back to XingRoute'}
          </Link>
        </div>
      </div>
    </div>
  )
}

/// 单段：以「· 」开头的行按项目符号缩进，其余为整段。
function Paragraph({ line, muted }: { line: string; muted?: boolean }) {
  const color = muted ? 'text-[var(--xr-text-muted)]' : 'text-[var(--xr-text-primary)]'
  const base = `text-[13px] leading-relaxed ${color}`
  if (line.startsWith('· ')) {
    return (
      <p className={`flex gap-2 ${base}`}>
        <span aria-hidden className="select-none text-[var(--xr-text-muted)]">
          ·
        </span>
        <span>{line.slice(2)}</span>
      </p>
    )
  }
  return <p className={base}>{line}</p>
}
